import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../../context/SubscriptionContext';
import useSubscriptionApi from '../../hooks/useSubscriptionApi';
import PlanCard from './PlanCard';
import CurrentSubscriptionCard from './CurrentSubscriptionCard';
import { SUBSCRIPTION_STATUS } from '../../utils/constants';
import '../../styles/SubscriptionPlans.css';

/**
 * SubscriptionPlans Component
 *
 * Main subscription plans page showing:
 * - Current subscription status (if any)
 * - Available plans grid
 * - Plan selection/change functionality
 */
const SubscriptionPlans = () => {
    const navigate = useNavigate();

    // Context
    const {
        subscriptionData,
        refreshSubscriptionStatus,
        getSubscriptionStatusText,
        formatDate,
        isInGracePeriod
    } = useSubscription();

    // API hook
    const {
        loading: apiLoading,
        error: apiError,
        clearError,
        fetchPlans,
        createCheckoutSession,
        changePlan,
        openCustomerPortal
    } = useSubscriptionApi();

    // Local state
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [processingPlan, setProcessingPlan] = useState(null);

    // Sync API errors to local error state
    useEffect(() => {
        if (apiError) {
            setError(apiError);
        }
    }, [apiError]);

    // Fetch plans on mount
    useEffect(() => {
        loadPlans();
    }, []);

    /**
     * Load subscription plans
     */
    const loadPlans = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const fetchedPlans = await fetchPlans();
            setPlans(fetchedPlans);
        } catch (err) {
            // Error already handled by hook
        } finally {
            setLoading(false);
        }
    }, [fetchPlans]);

    /**
     * Handle plan selection
     */
    const handleSelectPlan = useCallback(async (plan) => {
        try {
            setProcessingPlan(plan.id);
            setError(null);

            const isActivePlan = subscriptionData?.status === SUBSCRIPTION_STATUS.ACTIVE;
            const isDifferentPlan = subscriptionData?.priceId !== plan.priceId;

            // If user has active subscription, this is a plan change
            if (isActivePlan && isDifferentPlan) {
                const confirmChange = window.confirm(
                    `Czy chcesz zmienić plan na "${plan.name}"? Zmiana zostanie rozliczona proporcjonalnie.`
                );

                if (!confirmChange) {
                    setProcessingPlan(null);
                    return;
                }

                await changePlan(plan.priceId);
                alert('Plan został zmieniony pomyślnie!');
                await refreshSubscriptionStatus();
            } else {
                // New subscription - create checkout session
                await createCheckoutSession(plan.priceId);
            }
        } catch (err) {
            setError(err.message || 'Nie udało się przetworzyć żądania.');
        } finally {
            setProcessingPlan(null);
        }
    }, [subscriptionData, changePlan, createCheckoutSession, refreshSubscriptionStatus]);

    /**
     * Handle opening customer portal
     */
    const handleOpenPortal = useCallback(async () => {
        try {
            setLoading(true);
            await openCustomerPortal();
        } catch (err) {
            setError('Nie udało się otworzyć portalu płatności.');
        } finally {
            setLoading(false);
        }
    }, [openCustomerPortal]);

    /**
     * Check if plan is the current active plan
     */
    const isCurrentPlan = (plan) => {
        return (
            subscriptionData?.priceId === plan.priceId &&
            subscriptionData?.status === SUBSCRIPTION_STATUS.ACTIVE
        );
    };

    /**
     * Clear error message
     */
    const handleClearError = () => {
        setError(null);
        clearError();
    };

    // Loading state
    if (loading && plans.length === 0) {
        return (
            <div className="subscription-container">
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Ładowanie planów...</p>
                </div>
            </div>
        );
    }

    const hasActiveSubscription = subscriptionData?.status === SUBSCRIPTION_STATUS.ACTIVE;

    return (
        <div className="subscription-container">
            {/* Header */}
            <header className="subscription-header">
                <button
                    className="back-btn"
                    onClick={() => navigate('/dashboard')}
                >
                    ←
                </button>
                <h1>Plany Subskrypcji</h1>
                <p>Wybierz plan dopasowany do Twoich potrzeb</p>
            </header>

            {/* Error banner */}
            {error && (
                <div className="error-banner">
                    <p>{error}</p>
                    <button onClick={handleClearError}>×</button>
                </div>
            )}

            {/* Current subscription status */}
            <CurrentSubscriptionCard
                subscriptionData={subscriptionData}
                isInGracePeriod={isInGracePeriod()}
                onOpenPortal={handleOpenPortal}
                getStatusText={getSubscriptionStatusText}
                formatDate={formatDate}
            />

            {/* Plans grid */}
            {plans.length === 0 ? (
                <div className="empty-state">
                    <p>Brak dostępnych planów.</p>
                    <button onClick={loadPlans}>Odśwież</button>
                </div>
            ) : (
                <div className="plans-grid">
                    {plans.map((plan) => (
                        <PlanCard
                            key={plan.id}
                            plan={plan}
                            isCurrentPlan={isCurrentPlan(plan)}
                            isProcessing={processingPlan === plan.id}
                            hasActiveSubscription={hasActiveSubscription}
                            onSelect={handleSelectPlan}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default SubscriptionPlans;
